// ** React Imports
import { useEffect, useState, useRef } from "react"
import HCaptcha from "@hcaptcha/react-hcaptcha"

// ** Hooks Imports
import { useNavigate } from "react-router-dom"

// ** Styles Imports
import Styles from "./yourBitMatters.module.css"

// ** Components Imports
import Input from "../../components/Input/Input"
import Button from "../../components/Button/Button"
import PageHeader from "../../components/PageHeader/PageHeader"
import Honeypot from "../../components/Honeypot"

// form validations
import * as Yup from "yup"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// ** Actions Imports
import { _addForm, _getRequestTypes } from "./redux/actions"

// ** Utils Imports
import { showErrorSnackbar, showSuccessSnackbar } from "../snackbar/helpers"
import { useSelector } from "react-redux"

// login schema
const schema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  fullName: Yup.string().required("You full name is required"),
  requestType: Yup.string().required("Required"),
  phoneNumber: Yup.string()
    .notOneOf(["+"], "Invalid phone number")
    .matches(/^(?:\D*\d){10,}\D*$/, "Invalid phone number L")
    .required("Your phone number is required"),
  comments: Yup.string().required("Message is required")
})

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-ffffffffffff' // Test key

function YourBitMatters() {
  // ** Hooks
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onSubmit"
  })
  const navigate = useNavigate()

  // ** States
  const [requestTypes, setRequestTypes] = useState([])
  const [recaptchaConfirmed, setRecaptchaConfirmed] = useState(false)
  const [recaptchaResponse, setRecaptchaResponse] = useState("")
  const [requestType, setRequestType] = useState(null)
  const [pendingData, setPendingData] = useState(null)
  const captchaRef = useRef(null)
  const exchangeName = useSelector((state) => state.exchangeCustomization.value.name)

  const theme = useSelector((state) => state.theme.value)
  document.title = `${exchangeName}: Support`

  useEffect(() => {
    _getRequestTypes(
      (data) => setRequestTypes(data.data),
      () => showErrorSnackbar({ alertMessage: "Something went wrong" })
    )
  }, [])

  const performSubmit = (formData, token) => {
    _addForm(
      {
        full_name: formData.fullName,
        email: formData.email,
        request_id: requestType,
        phone_number: formData.phoneNumber,
        comment: formData.comments,
        hcaptcha_token: token
      },
      () => {
        showSuccessSnackbar({ alertMessage: "Form submitted" })
        navigate("/")
        setPendingData(null)
        setRecaptchaConfirmed(false)
        setRecaptchaResponse("")
      },
      () => {
        showErrorSnackbar({ alertMessage: "Something went wrong" })
        setPendingData(null)
        setRecaptchaConfirmed(false)
        setRecaptchaResponse("")
        captchaRef.current.resetCaptcha()
      }
    )
  }

  const onHCaptchaVerify = (token) => {
    setRecaptchaConfirmed(true)
    setRecaptchaResponse(token)
    if (pendingData) {
      performSubmit(pendingData, token)
    }
  }

  const onHCaptchaError = (error) => {
    setRecaptchaConfirmed(false)
    setRecaptchaResponse("")
    setPendingData(null)
    showErrorSnackbar({ alertMessage: "CAPTCHA verification failed. Please try again." })
  }

  const onHCaptchaExpire = () => {
    setRecaptchaConfirmed(false)
    setRecaptchaResponse("")
    setPendingData(null)
    showErrorSnackbar({ alertMessage: "CAPTCHA expired. Please verify again." })
  }

  const onSubmit = (formData) => {
    if (formData.company_name) {
      return
    }
    if (!recaptchaConfirmed) {
      setPendingData(formData)
      captchaRef.current.execute()
    } else {
      performSubmit(formData, recaptchaResponse)
    }
  }

  return (
    <div className={Styles.pageContainer}>
      <PageHeader
        title="Your tech matters"
        sectionTitle="Support"
        description={
          <>
            We're in this together, and that means open communication is
            everything. We want to hear from you - your questions, your ideas,
            and your thoughts on our exchange. Because at WealthTech, we're not just
            building a platform but a community. So, speak up and let your voice
            be heard. Together, we'll conquer the crypto world.
          </>
        }
      />

      <form className={Styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
        <Honeypot register={register} />
        <div className={Styles.row}>
          <Input
            inputLabel="Full Name"
            variant="secondary"
            placeholder="Alex Johnson"
            {...register("fullName")}
            error={errors.fullName}
          />
          <Input
            inputLabel="Email"
            variant="secondary"
            placeholder="example@mail.com"
            {...register("email")}
            error={errors.email}
          />
        </div>
        <div className={Styles.row}>
          <Input
            type="select"
            inputLabel="Type of request"
            placeholder="Select a type"
            selectOptions={requestTypes.map((el) => ({
              label: el.request_type,
              value: el.id
            }))}
            error={errors.requestType}
            onChange={(e, value) => {
              setValue("requestType", value)
              setRequestType(value)
            }}
            selectValue={requestType}
          />
          <Input
            inputLabel="Phone Number"
            type="phoneNumber"
            inputIconContainerStyle={{
              backgroundColor: "var(--background-color)"
            }}
            register={{ ...register("phoneNumber") }}
            error={errors.phoneNumber}
          />
        </div>
        <Input
          type="textarea"
          inputLabel="Comments"
          variant="secondary"
          placeholder="Speak your mind"
          {...register("comments")}
          error={errors.comments}
        />

        <HCaptcha
          id="hcaptcha-bit-matters"
          ref={captchaRef}
          size="invisible"
          sitekey={HCAPTCHA_SITE_KEY}
          onVerify={onHCaptchaVerify}
          onError={onHCaptchaError}
          onExpire={onHCaptchaExpire}
          theme={theme}
        />

        <Button
          type="submit"
          text="Submit"
          style={{ width: 220, margin: "0 auto" }}
        />
      </form>
    </div>
  )
}

export default YourBitMatters
